<script lang="ts" setup>
import { i18n } from '@/i18n';
import TableHead from '../TableHead.vue';
import { TableContentProperty } from '../../interface/tableContentHeaders';
import { BaseTableContentViewModel } from '../../baseTableContentViewModel';
import ArrowIcon from '../../components/ArrowIcon.vue';
import OrderStatusBadge from '@/corePagesModule/components/OrderStatusBadge.vue';
import HelpIconWithModal from '@/corePagesModule/components/HelpIconWithModal.vue';
import { orderStatusDescriptions } from '@/corePagesModule/BaseTableContent/components/order/orderStatus';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    header: TableContentProperty;
    headerIndex: number;
    accumulationTableContentWidth: number;
    isVariableWidth: boolean;
    isAllSelected: boolean;
    stableColumnCount: number;
    isOrderGroupEditMode?: boolean;
    editingId: string;
  }>(),
  {}
);

const baseTableContentVM = new BaseTableContentViewModel();

// emits
const emits = defineEmits<{
  (e: 'onClickSort', index: number, key: TableContentProperty, isAsc: boolean): void;
  (e: 'onMousedownResizer', event: MouseEvent, headerIndex: number): void;
}>();

//変数
const t = i18n.t;

const isPaddingAvailCond = computed(() => {
  switch (props.header.key) {
    case 'OrderEditButton':
      return props.isOrderGroupEditMode;
    case 'OrderDeleteButton':
      return !props.editingId;
    default:
      return false;
  }
});
</script>

<template>
  <TableHead
    :class="[baseTableContentVM.isSticky(stableColumnCount, headerIndex) ? 'sticky-content' : '', isPaddingAvailCond ? 'no-padding' : '']"
    :style="{
      left: baseTableContentVM.calcStickyDiff(stableColumnCount, headerIndex, accumulationTableContentWidth),
    }"
    :index="headerIndex"
    :resizable="
      // 幅可変な列 && 固定する列に含まれていない -> 可変幅
      header.isVariableWidth && !baseTableContentVM.isSticky(stableColumnCount, headerIndex)
    "
    :data-key="header.key"
    @on-mousedown-resizer="($event) => emits('onMousedownResizer', $event, headerIndex)"
  >
    <template v-if="header.sortOption.isSortable">
      <div class="table-header__sortable-content">
        <div class="table-header__text">{{ t(header.key) }}</div>
        <ArrowIcon :asc="header.sortOption.isAsc" @on-click="emits('onClickSort', headerIndex, header, header.sortOption.isAsc)" />
      </div>
    </template>
    <template v-else-if="header.key === 'OrderRecommendStatus'">
      <div class="order-alert-header">
        <span class="order-alert-header__title"> {{ t(header.key) }} </span>
        <HelpIconWithModal :use-transform="true">
          <div class="order-status-content">
            <div class="order-status-content__header order-status-header">
              <span class="order-status-header__title">
                {{ t('ステータスとは') }}
              </span>
              <span class="order-status-header__description">
                {{ t('LEANPATHでは発注に関するステータスが10種類あります。') }}
              </span>
            </div>
            <div class="order-status-content__order-status order-status">
              <template v-for="(status, index) in orderStatusDescriptions" :key="index">
                <div class="order-status__container">
                  <OrderStatusBadge class="order-status__container--badge" :order-status-str="status.status" />
                  <span class="order-status__container--description">{{ t(status.description) }}</span>
                </div>
              </template>
            </div>
          </div>
        </HelpIconWithModal>
      </div>
    </template>
    <template v-else-if="header.key === 'OrderEditButton'" />
    <template v-else>
      <div class="table-header__text">{{ t(header.key) }}</div>
    </template>
  </TableHead>
</template>

<style lang="scss" scoped>
.order-alert-header {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  &__title {
    margin-right: 4px;
  }
}

.order-status-content {
  display: grid;
  grid-template-rows: fit-content(20%) 1fr;
  min-height: 600px; // TODO: .order-statusの制約だと親はみ出ちゃうから仕方なく。。
  &__header {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 16px;
  }
  &__order-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 16px;
  }
}

.order-status-header {
  display: grid;
  grid-template-rows: 1fr 1fr;
  align-items: center;
  justify-items: center;
  gap: 16px;
  &__title {
    font-size: 22px;
    font-weight: bold;
    line-height: 28px;
  }
  &__description {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
  }
}

.order-status {
  display: grid;
  grid-template-columns: repeat(3, minmax(172px, 1fr));
  grid-template-rows: repeat(auto-fit, 120px);
  gap: 8px;
  &__container {
    position: relative;
    height: 100%;
    display: grid;
    grid-template-rows: 1fr 1fr;
    align-items: center;
    justify-items: center;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    padding: 16px 12px 16px 12px;
    &--badge {
      width: 100%;
      height: 100%;
    }
    &--description {
      justify-self: start;
      font-size: 12px;
      line-height: 14px;
      font-weight: 400;
    }
  }
}

.no-padding {
  padding: 0;
}
</style>
